import * as modalComponents from '@provider-directory/components/modals';
import type { DecisionTreeDisclaimerSceneProps } from '@provider-directory/components/modals/decision-tree-disclaimer-scene-props.types';
import type { DecisionTreeErrorSceneProps } from '@provider-directory/components/modals/decision-tree-error-scene-props.types';
import * as sceneComponents from '@provider-directory/components/scenes';
import { PartialAppointmentType } from '@provider-directory/contexts/AppointmentContext';
import { EMPTY_INSURANCE_FIELDS } from '@provider-directory/models/appointment.model';
import {
  DecisionTreeFormSceneSlug,
  DecisionTreeQuestionSceneProps,
  DecisionTreeSceneSlug,
  Scene,
  SceneSlug,
  SceneValidation,
  SpecialtyBranch,
} from '@provider-directory/models/decision-tree.model';

interface DecisionTreeQuestionScene extends Scene {
  type: 'decisionTreeQuestion';
  props: DecisionTreeQuestionSceneProps;
}

export interface DecisionTreeFormSceneProps {
  progress: number;
  name: DecisionTreeFormSceneSlug;
  text: string;
  prevScene: DecisionTreeSceneSlug | null;
  nextScene: SceneSlug;
  validation?: SceneValidation | SceneValidation[];
}
interface DecisionTreeFormScene extends Scene {
  type: 'decisionTreeForm';
  props: DecisionTreeFormSceneProps;
}

interface DecisionTreeErrorScene extends Scene {
  type: 'decisionTreeError';
  props: DecisionTreeErrorSceneProps;
}
interface DecisionTreeDisclaimerScene extends Scene {
  type: 'decisionTreeDisclaimer';
  props: DecisionTreeDisclaimerSceneProps;
}

export type DecisionTreeScene =
  | DecisionTreeQuestionScene
  | DecisionTreeFormScene
  | DecisionTreeErrorScene
  | DecisionTreeDisclaimerScene;

export interface ScheduleContext {
  updateAppointment: (patch: PartialAppointmentType) => void;
}

export interface ScheduleScene extends Scene {
  showProgressBar: boolean;
  shouldShow: boolean;
  sceneName: string;
  onBreadcrumbClick?: (context: ScheduleContext) => void;
}

const decisionTreeScenesCache: Record<
  string,
  Record<
    SpecialtyBranch,
    Partial<Record<DecisionTreeSceneSlug, DecisionTreeScene>>
  >
> = {};

export const getDecisionTreeScenes = (): Record<
  SpecialtyBranch,
  Partial<Record<DecisionTreeSceneSlug, DecisionTreeScene>>
> => {
  const cacheKey = 'default';
  const cachedScenes = decisionTreeScenesCache[cacheKey];

  if (cachedScenes !== undefined) {
    return cachedScenes;
  }

  const scenes: Record<
    SpecialtyBranch,
    Partial<Record<DecisionTreeSceneSlug, DecisionTreeScene>>
  > = {
    cardiology: {
      'is-adult': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'is-adult',
        props: {
          progress: 33,
          name: 'is-adult',
          text: 'decisionTree.isAdult.question',
          warning: 'decisionTree.isAdult.emergencySymptoms',
          prevScene: null,
          options: [
            {
              value: 'under18',
              label: 'decisionTree.isAdult.under18',
              nextScene: 'is-adult-cardiology-error',
            },
            {
              value: 'over18',
              label: 'decisionTree.isAdult.over18',
              nextScene: 'seen-cardiology-recently',
            },
          ],
        },
      },
      'is-adult-cardiology-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'is-adult-cardiology-error',
        props: {
          icon: 'error_fill',
          title: 'decisionTree.isAdultCardiologyError.title',
          body: 'decisionTree.isAdultCardiologyError.body',
          specialty: 'decisionTree.specialties.cardiology',
          ctaButtons: ['back-to-search'],
          progress: 100,
          name: 'is-adult-cardiology-error',
          prevScene: 'is-adult',
        },
      },
      'seen-cardiology-recently': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'seen-cardiology-recently',
        props: {
          progress: 56,
          name: 'seen-cardiology-recently',
          text: 'decisionTree.seenProviderRecently.question',
          specialty: 'decisionTree.specialties.cardiology',
          prevScene: 'is-adult',
          options: [
            {
              value: 'yes',
              label: 'yes',
              nextScene: 'seen-cardiology-recently-error',
            },
            { value: 'no', label: 'no', nextScene: 'reason-for-visit' },
          ],
        },
      },
      'seen-cardiology-recently-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'seen-cardiology-recently-error',
        props: {
          progress: 100,
          icon: 'error_fill',
          title: 'decisionTree.seenCardiologyRecentlyError.title',
          body: 'decisionTree.seenCardiologyRecentlyError.body',
          specialty: 'decisionTree.specialties.cardiology',
          ctaButtons: ['login-to-mychart', 'back-to-search'],
          name: 'seen-cardiology-recently-error',
          prevScene: 'seen-cardiology-recently',
        },
      },
      'reason-for-visit': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'reason-for-visit',
        props: {
          progress: 73,
          name: 'reason-for-visit',
          text: 'decisionTree.reasonForVisit.question',
          prevScene: 'seen-cardiology-recently',
          action: 'setRfv',
          options: [
            {
              value: 'general-cardiology-evaluation',
              label:
                'decisionTree.reasonForVisit.general-cardiology-evaluation.label',
              subCopy:
                'decisionTree.reasonForVisit.general-cardiology-evaluation.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'evaluation-for-a-heart-condition',
              label:
                'decisionTree.reasonForVisit.evaluation-for-a-heart-condition.label',
              subCopy:
                'decisionTree.reasonForVisit.evaluation-for-a-heart-condition.description',
              nextScene: 'reason-for-visit-error',
            },
          ],
        },
      },
      'reason-for-visit-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'reason-for-visit-error',
        props: {
          progress: 100,
          icon: 'error_fill',
          title: 'decisionTree.reasonForVisitError.title',
          body: 'decisionTree.reasonForVisitError.body',
          ctaButtons: ['back-to-search'],
          name: 'reason-for-visit-error',
          prevScene: 'reason-for-visit',
        },
      },
      'reason-for-visit-provider-error': {
        type: 'decisionTreeError',
        component: modalComponents.RfvMismatchModal,
        sceneSlug: 'reason-for-visit-provider-error',
        props: {
          progress: 100,
          icon: 'error_fill',
          title: 'decisionTree.reasonForVisitProviderError.title',
          body: 'decisionTree.reasonForVisitProviderError.body',
          specialty: 'decisionTree.specialties.cardiology',
          ctaButtons: ['back-to-search-with-rfv'],
          name: 'reason-for-visit-provider-error',
          prevScene: 'reason-for-visit',
        },
      },
      'select-time-modal': {
        type: 'decisionTreeForm',
        component: modalComponents.DecisionTreeForm,
        sceneSlug: 'select-time-modal',
        props: {
          progress: 82,
          name: 'select-time-modal',
          text: 'decisionTree.scheduleAppointmentWith',
          prevScene: 'reason-for-visit',
          nextScene: 'patient-info',
        },
      },
    },
    dermatology: {
      'is-adult': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'is-adult',
        props: {
          progress: 33,
          name: 'is-adult',
          text: 'decisionTree.isAdult.question',
          warning: 'decisionTree.isAdult.emergencySymptoms',
          prevScene: null,
          options: [
            {
              value: 'under18',
              label: 'decisionTree.isAdult.under18',
              nextScene: 'is-adult-dermatology-error',
            },
            {
              value: 'over18',
              label: 'decisionTree.isAdult.over18',
              nextScene: 'seen-dermatology-recently',
            },
          ],
        },
      },
      'is-adult-dermatology-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'is-adult-dermatology-error',
        props: {
          icon: 'error_fill',
          title: 'decisionTree.isAdultDermatologyError.title',
          body: 'decisionTree.isAdultDermatologyError.body',
          specialty: 'decisionTree.specialties.dermatology',
          ctaButtons: ['back-to-search'],
          progress: 100,
          name: 'is-adult-dermatology-error',
          prevScene: 'is-adult',
        },
      },
      'seen-dermatology-recently': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'seen-dermatology-recently',
        props: {
          progress: 56,
          name: 'seen-dermatology-recently',
          text: 'decisionTree.seenProviderRecently.question',
          specialty: 'decisionTree.specialties.dermatology',
          prevScene: 'is-adult',
          options: [
            {
              value: 'yes',
              label: 'yes',
              nextScene: 'seen-dermatology-recently-error',
            },
            { value: 'no', label: 'no', nextScene: 'reason-for-visit' },
          ],
        },
      },
      'seen-dermatology-recently-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'seen-dermatology-recently-error',
        props: {
          progress: 100,
          icon: 'error_fill',
          title: 'decisionTree.seenDermatologyRecentlyError.title',
          body: 'decisionTree.seenDermatologyRecentlyError.body',
          specialty: 'decisionTree.specialties.dermatology',
          ctaButtons: ['login-to-mychart', 'back-to-search'],
          name: 'seen-dermatology-recently-error',
          prevScene: 'seen-dermatology-recently',
        },
      },
      'reason-for-visit': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'reason-for-visit',
        props: {
          progress: 73,
          name: 'reason-for-visit',
          text: 'decisionTree.reasonForVisit.question',
          prevScene: 'seen-dermatology-recently',
          action: 'setRfv',
          options: [
            {
              value: 'acne',
              label: 'decisionTree.reasonForVisit.acne.label',
              subCopy: 'decisionTree.reasonForVisit.acne.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'psoriasis',
              label: 'decisionTree.reasonForVisit.psoriasis.label',
              subCopy: 'decisionTree.reasonForVisit.psoriasis.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'cosmetics',
              label: 'decisionTree.reasonForVisit.cosmetics.label',
              subCopy: 'decisionTree.reasonForVisit.cosmetics.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'general-derm',
              label: 'decisionTree.reasonForVisit.general-derm.label',
              subCopy: 'decisionTree.reasonForVisit.general-derm.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'treatment-for-skin-cancer',
              label:
                'decisionTree.reasonForVisit.treatment-for-skin-cancer.label',
              subCopy:
                'decisionTree.reasonForVisit.treatment-for-skin-cancer.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'skin-check',
              label: 'decisionTree.reasonForVisit.skin-check.label',
              subCopy: 'decisionTree.reasonForVisit.skin-check.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
          ],
        },
      },
      'reason-for-visit-provider-error': {
        type: 'decisionTreeError',
        component: modalComponents.RfvMismatchModal,
        sceneSlug: 'reason-for-visit-provider-error',
        props: {
          progress: 100,
          icon: 'error_fill',
          title: 'decisionTree.reasonForVisitProviderError.title',
          body: 'decisionTree.reasonForVisitProviderError.body',
          specialty: 'decisionTree.specialties.dermatology',
          ctaButtons: ['back-to-search-with-rfv'],
          name: 'reason-for-visit-provider-error',
          prevScene: 'reason-for-visit',
        },
      },
      'select-time-modal': {
        type: 'decisionTreeForm',
        component: modalComponents.DecisionTreeForm,
        sceneSlug: 'select-time-modal',
        props: {
          progress: 82,
          name: 'select-time-modal',
          text: 'decisionTree.scheduleAppointmentWith',
          prevScene: 'reason-for-visit',
          nextScene: 'patient-info',
        },
      },
    },
    allergy: {
      'is-adult': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'is-adult',
        props: {
          progress: 33,
          name: 'is-adult',
          text: 'decisionTree.isAdult.question',
          warning: 'decisionTree.isAdult.emergencySymptoms',
          prevScene: null,
          options: [
            {
              value: 'under16',
              label: 'decisionTree.isAdult.under16',
              nextScene: 'is-adult-allergy-error',
            },
            {
              value: 'over16',
              label: 'decisionTree.isAdult.over16',
              nextScene: 'seen-allergy-recently',
            },
          ],
        },
      },
      'is-adult-allergy-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'is-adult-allergy-error',
        props: {
          icon: 'error_fill',
          title: 'decisionTree.isAdultAllergyError.title',
          body: 'decisionTree.isAdultAllergyError.body',
          specialty: 'decisionTree.specialties.allergy',
          ctaButtons: ['back-to-search'],
          progress: 100,
          name: 'is-adult-allergy-error',
          prevScene: 'is-adult',
        },
      },
      'seen-allergy-recently': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'seen-allergy-recently',
        props: {
          progress: 56,
          name: 'seen-allergy-recently',
          text: 'decisionTree.seenProviderRecently.allergyQuestion',
          specialty: 'decisionTree.specialties.allergy',
          prevScene: 'is-adult',
          options: [
            {
              value: 'yes',
              label: 'yes',
              nextScene: 'seen-allergy-recently-error',
            },
            { value: 'no', label: 'no', nextScene: 'reason-for-visit' },
          ],
        },
      },
      'seen-allergy-recently-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'seen-allergy-recently-error',
        props: {
          progress: 100,
          icon: 'error_fill',
          title: 'decisionTree.seenAllergyRecentlyError.title',
          body: 'decisionTree.seenAllergyRecentlyError.body',
          specialty: 'decisionTree.specialties.allergy',
          ctaButtons: ['login-to-mychart', 'back-to-search'],
          name: 'seen-allergy-recently-error',
          prevScene: 'seen-allergy-recently',
        },
      },
      'reason-for-visit': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'reason-for-visit',
        props: {
          progress: 73,
          name: 'reason-for-visit',
          text: 'decisionTree.reasonForVisit.question',
          prevScene: 'seen-allergy-recently',
          action: 'setRfv',
          options: [
            {
              value: 'asthma',
              label: 'decisionTree.reasonForVisit.asthma.label',
              subCopy: 'decisionTree.reasonForVisit.asthma.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'medication-allergies',
              label: 'decisionTree.reasonForVisit.medication-allergies.label',
              subCopy:
                'decisionTree.reasonForVisit.medication-allergies.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'food-allergies',
              label: 'decisionTree.reasonForVisit.food-allergies.label',
              subCopy: 'decisionTree.reasonForVisit.food-allergies.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'general-allergy',
              label: 'decisionTree.reasonForVisit.general-allergy.label',
              subCopy:
                'decisionTree.reasonForVisit.general-allergy.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'immunology',
              label: 'decisionTree.reasonForVisit.immunology.label',
              subCopy: 'decisionTree.reasonForVisit.immunology.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
          ],
        },
      },
      'reason-for-visit-provider-error': {
        type: 'decisionTreeError',
        component: modalComponents.RfvMismatchModal,
        sceneSlug: 'reason-for-visit-provider-error',
        props: {
          progress: 100,
          icon: 'error_fill',
          title: 'decisionTree.reasonForVisitProviderError.title',
          body: 'decisionTree.reasonForVisitProviderError.body',
          specialty: 'decisionTree.specialties.allergy',
          ctaButtons: ['back-to-search-with-rfv'],
          name: 'reason-for-visit-provider-error',
          prevScene: 'reason-for-visit',
        },
      },
      'select-time-modal': {
        type: 'decisionTreeForm',
        component: modalComponents.DecisionTreeForm,
        sceneSlug: 'select-time-modal',
        props: {
          progress: 82,
          name: 'select-time-modal',
          text: 'decisionTree.scheduleAppointmentWith',
          prevScene: 'reason-for-visit',
          nextScene: 'patient-info',
          validation: {
            type: 'rfvAllergyDisclaimer',
            failedValidationScene: 'disclaimer',
          },
        },
      },
      disclaimer: {
        type: 'decisionTreeDisclaimer',
        component: modalComponents.DecisionTreeDisclaimer,
        sceneSlug: 'disclaimer',
        props: {
          progress: 95,
          name: 'disclaimer',
          title: 'decisionTree.allergyInPersonDisclaimer.title',
          body: 'decisionTree.allergyInPersonDisclaimer.body',
          prevScene: 'select-time-modal',
          nextScene: 'patient-info',
          nextButtonText:
            'decisionTree.allergyInPersonDisclaimer.nextButtonText',
        },
      },
    },
    endocrinology: {
      'is-adult': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'is-adult',
        props: {
          progress: 25,
          name: 'is-adult',
          text: 'decisionTree.isAdult.endocrinologyQuestion',
          warning: 'decisionTree.isAdult.emergencySymptoms',
          prevScene: null,
          options: [
            {
              value: 'under18',
              label: 'decisionTree.isAdult.under18',
              nextScene: 'is-adult-endocrinology-error',
            },
            {
              value: 'between18and35',
              label: 'decisionTree.isAdult.between18and35',
              nextScene: 'seen-endocrinology-recently',
            },
            {
              value: 'over36',
              label: 'decisionTree.isAdult.over36',
              nextScene: 'seen-endocrinology-recently',
            },
          ],
        },
      },
      'is-adult-endocrinology-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'is-adult-endocrinology-error',
        props: {
          icon: 'error_fill',
          title: 'decisionTree.isAdultEndocrinologyError.title',
          body: 'decisionTree.isAdultEndocrinologyError.body',
          specialty: 'decisionTree.specialties.endocrinology',
          ctaButtons: ['back-to-search'],
          progress: 100,
          name: 'is-adult-endocrinology-error',
          prevScene: 'is-adult',
        },
      },
      'seen-endocrinology-recently': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'seen-endocrinology-recently',
        props: {
          progress: 40,
          name: 'seen-endocrinology-recently',
          text: 'decisionTree.seenProviderRecently.endocrinologyQuestion',
          specialty: 'decisionTree.specialties.endocrinology',
          prevScene: 'is-adult',
          options: [
            {
              value: 'yes',
              label: 'yes',
              nextScene: 'seen-endocrinology-recently-error',
            },
            {
              value: 'no',
              label: 'no',
              nextScene: 'endocrinology-reason-for-visit',
            },
          ],
        },
      },
      'seen-endocrinology-recently-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'seen-endocrinology-recently-error',
        props: {
          progress: 100,
          icon: 'error_fill',
          title: 'decisionTree.seenEndocrinologyRecentlyError.title',
          body: 'decisionTree.seenEndocrinologyRecentlyError.body',
          specialty: 'decisionTree.specialties.endocrinology',
          ctaButtons: ['login-to-mychart', 'back-to-search'],
          name: 'seen-endocrinology-recently-error',
          prevScene: 'seen-endocrinology-recently',
        },
      },
      'endocrinology-reason-for-visit': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'endocrinology-reason-for-visit',
        props: {
          progress: 55,
          name: 'endocrinology-reason-for-visit',
          text: 'decisionTree.reasonForVisit.question',
          prevScene: 'seen-endocrinology-recently',
          options: [
            {
              value: 'diabetes-care',
              label: 'decisionTree.reasonForVisit.diabetes-care.label',
              subCopy: 'decisionTree.reasonForVisit.diabetes-care.description',
              nextScene: 'endocrinology-diabetes-type',
            },
            {
              value: 'thyroid-concerns',
              label: 'decisionTree.reasonForVisit.thyroid-concerns.label',
              nextScene: 'endocrinology-thyroid-type',
            },
            {
              value: 'pregnancy-or-fertility',
              label: 'decisionTree.reasonForVisit.pregnancy-or-fertility.label',
              nextScene: 'endocrinology-pregnancy-question',
            },
            {
              value: 'weight-management',
              label: 'decisionTree.reasonForVisit.weight-management.label',
              nextScene: 'select-time-modal',
              action: 'setRfv',
              validation: [
                {
                  type: 'weightManagementHospitalCheck',
                  failedValidationScene:
                    'endocrinology-weight-management-check-error',
                },
                {
                  type: 'rfvSupportedByProvider',
                  failedValidationScene: 'reason-for-visit-provider-error',
                },
              ],
            },
            {
              value: 'something-else-endocrinology',
              label:
                'decisionTree.reasonForVisit.something-else-endocrinology.label',
              subCopy:
                'decisionTree.reasonForVisit.something-else-endocrinology.description',
              nextScene: 'endocrinology-something-else',
            },
          ],
        },
      },
      'endocrinology-diabetes-type': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'endocrinology-diabetes-type',
        props: {
          progress: 70,
          name: 'endocrinology-diabetes-type',
          text: 'decisionTree.reasonForVisit.diabetes-care.question',
          prevScene: 'endocrinology-reason-for-visit',
          action: 'setRfv',
          options: [
            {
              value: 'type-1-diabetes',
              label: 'decisionTree.reasonForVisit.type-1-diabetes.label',
              nextScene: 'select-time-modal',
              action: 'rfvAddAgeGroup',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'type-2-diabetes',
              label: 'decisionTree.reasonForVisit.type-2-diabetes.label',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'diabetes-unknown',
              label: 'decisionTree.reasonForVisit.diabetes-unknown.label',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
          ],
        },
      },
      'endocrinology-thyroid-type': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'endocrinology-thyroid-type',
        props: {
          progress: 70,
          name: 'endocrinology-thyroid-type',
          text: 'decisionTree.reasonForVisit.thyroid-concerns.question',
          prevScene: 'endocrinology-reason-for-visit',
          action: 'setRfv',
          options: [
            {
              value: 'thyroid-cancer',
              label: 'decisionTree.reasonForVisit.thyroid-cancer.label',
              subCopy: 'decisionTree.reasonForVisit.thyroid-cancer.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'thyroid-condition',
              label: 'decisionTree.reasonForVisit.thyroid-condition.label',
              subCopy:
                'decisionTree.reasonForVisit.thyroid-condition.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
          ],
        },
      },
      'endocrinology-pregnancy-question': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'endocrinology-pregnancy-question',
        props: {
          progress: 70,
          name: 'endocrinology-pregnancy-question',
          text: 'decisionTree.reasonForVisit.pregnancy-or-fertility.question',
          prevScene: 'endocrinology-reason-for-visit',
          options: [
            {
              value: 'yes',
              label: 'yes',
              nextScene: 'endocrinology-pregnancy-error',
            },
            {
              value: 'no',
              label: 'no',
              nextScene: 'endocrinology-fertility-error',
            },
          ],
        },
      },
      'endocrinology-pregnancy-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'endocrinology-pregnancy-error',
        props: {
          icon: 'error_fill',
          title: 'decisionTree.endocrinologyPregnancyError.title',
          body: 'decisionTree.endocrinologyPregnancyError.body',
          ctaButtons: ['back-to-search'],
          progress: 100,
          name: 'endocrinology-pregnancy-error',
          prevScene: 'endocrinology-pregnancy-question',
        },
      },
      'endocrinology-fertility-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'endocrinology-fertility-error',
        props: {
          icon: 'error_fill',
          title: 'decisionTree.endocrinologyFertilityError.title',
          body: 'decisionTree.endocrinologyFertilityError.body',
          ctaButtons: ['back-to-search'],
          progress: 100,
          name: 'endocrinology-fertility-error',
          prevScene: 'endocrinology-pregnancy-question',
        },
      },
      'endocrinology-weight-management-check-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'endocrinology-weight-management-check-error',
        props: {
          icon: 'error_fill',
          title: 'decisionTree.endocrinologyWeightManagementError.title',
          body: 'decisionTree.endocrinologyWeightManagementError.body',
          ctaButtons: ['back-to-search'],
          progress: 100,
          name: 'endocrinology-weight-management-check-error',
          prevScene: 'endocrinology-reason-for-visit',
        },
      },
      'endocrinology-something-else': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'endocrinology-something-else',
        props: {
          progress: 70,
          name: 'endocrinology-something-else',
          text: 'decisionTree.reasonForVisit.something-else-endocrinology.question',
          prevScene: 'endocrinology-reason-for-visit',
          action: 'setRfv',
          options: [
            {
              value: 'adrenal-or-pituitary-gland-conditions',
              label:
                'decisionTree.reasonForVisit.adrenal-or-pituitary-gland-conditions.label',
              subCopy:
                'decisionTree.reasonForVisit.adrenal-or-pituitary-gland-conditions.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'perimenopausal-or-postmenopausal-hormone-therapy',
              label:
                'decisionTree.reasonForVisit.perimenopausal-or-postmenopausal-hormone-therapy.label',
              subCopy:
                'decisionTree.reasonForVisit.perimenopausal-or-postmenopausal-hormone-therapy.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'gender-affirming-hormone-therapy',
              label:
                'decisionTree.reasonForVisit.gender-affirming-hormone-therapy.label',
              subCopy:
                'decisionTree.reasonForVisit.gender-affirming-hormone-therapy.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'other-hormone-bone-or-metabolism-concerns',
              label:
                'decisionTree.reasonForVisit.other-hormone-bone-or-metabolism-concerns.label',
              subCopy:
                'decisionTree.reasonForVisit.other-hormone-bone-or-metabolism-concerns.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
          ],
        },
      },
      'reason-for-visit-provider-error': {
        type: 'decisionTreeError',
        component: modalComponents.RfvMismatchModal,
        sceneSlug: 'reason-for-visit-provider-error',
        props: {
          progress: 100,
          icon: 'error_fill',
          title: 'decisionTree.reasonForVisitProviderError.title',
          body: 'decisionTree.reasonForVisitProviderError.body',
          specialty: 'decisionTree.specialties.endocrinology',
          ctaButtons: ['back-to-search-with-rfv'],
          name: 'reason-for-visit-provider-error',
          prevScene: 'endocrinology-reason-for-visit',
        },
      },
      'select-time-modal': {
        type: 'decisionTreeForm',
        component: modalComponents.DecisionTreeForm,
        sceneSlug: 'select-time-modal',
        props: {
          progress: 85,
          name: 'select-time-modal',
          text: 'decisionTree.scheduleAppointmentWith',
          prevScene: 'endocrinology-reason-for-visit',
          nextScene: 'patient-info',
        },
      },
    },
    gastroenterology: {
      'is-adult': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'is-adult',
        props: {
          progress: 25,
          name: 'is-adult',
          text: 'decisionTree.isAdult.gastroenterologyQuestion',
          warning: 'decisionTree.isAdult.emergencySymptoms',
          prevScene: null,
          options: [
            {
              value: 'under18',
              label: 'decisionTree.isAdult.under18',
              nextScene: 'is-adult-gastroenterology-error',
            },
            {
              value: 'over18',
              label: 'decisionTree.isAdult.over18',
              nextScene: 'seen-gastroenterology-recently',
            },
          ],
        },
      },
      'is-adult-gastroenterology-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'is-adult-gastroenterology-error',
        props: {
          icon: 'error_fill',
          title: 'decisionTree.isAdultGastroenterologyError.title',
          body: 'decisionTree.isAdultGastroenterologyError.body',
          specialty: 'decisionTree.specialties.gastroenterology',
          ctaButtons: ['back-to-search'],
          progress: 100,
          name: 'is-adult-gastroenterology-error',
          prevScene: 'is-adult',
        },
      },
      'seen-gastroenterology-recently': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'seen-gastroenterology-recently',
        props: {
          progress: 45,
          name: 'seen-gastroenterology-recently',
          text: 'decisionTree.seenProviderRecently.gastroenterologyQuestion',
          specialty: 'decisionTree.specialties.gastroenterology',
          prevScene: 'is-adult',
          options: [
            {
              value: 'yes',
              label: 'yes',
              nextScene: 'seen-gastroenterology-recently-error',
            },
            {
              value: 'no',
              label: 'no',
              nextScene: 'gastroenterology-reason-for-visit',
            },
          ],
        },
      },
      'seen-gastroenterology-recently-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'seen-gastroenterology-recently-error',
        props: {
          progress: 100,
          icon: 'error_fill',
          title: 'decisionTree.seenGastroenterologyRecentlyError.title',
          body: 'decisionTree.seenGastroenterologyRecentlyError.body',
          specialty: 'decisionTree.specialties.gastroenterology',
          ctaButtons: ['login-to-mychart', 'back-to-search'],
          name: 'seen-gastroenterology-recently-error',
          prevScene: 'seen-gastroenterology-recently',
        },
      },
      'gastroenterology-reason-for-visit': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'gastroenterology-reason-for-visit',
        props: {
          progress: 65,
          name: 'gastroenterology-reason-for-visit',
          text: 'decisionTree.reasonForVisit.question',
          prevScene: 'seen-gastroenterology-recently',
          action: 'setRfv',
          options: [
            {
              value: 'general-digestive-problems-or-questions',
              label:
                'decisionTree.reasonForVisit.general-digestive-problems-or-questions.label',
              subCopy:
                'decisionTree.reasonForVisit.general-digestive-problems-or-questions.description',
              nextScene: 'select-time-modal',
              tooltip:
                '<div class="space-y-2"><div class="space-y-1"><p class="text-body-medium">{{decisionTree.reasonForVisit.general-digestive-problems-or-questions.tooltip.description}}</p></div><ul class="list-disc space-y-1 pl-5 text-body-medium"><li>{{decisionTree.reasonForVisit.general-digestive-problems-or-questions.tooltip.items.heartburn}}</li><li>{{decisionTree.reasonForVisit.general-digestive-problems-or-questions.tooltip.items.bloating}}</li><li>{{decisionTree.reasonForVisit.general-digestive-problems-or-questions.tooltip.items.nausea}}</li><li>{{decisionTree.reasonForVisit.general-digestive-problems-or-questions.tooltip.items.diarrheaOrConstipation}}</li><li>{{decisionTree.reasonForVisit.general-digestive-problems-or-questions.tooltip.items.mildPain}}</li><li>{{decisionTree.reasonForVisit.general-digestive-problems-or-questions.tooltip.items.ibs}}</li><li>{{decisionTree.reasonForVisit.general-digestive-problems-or-questions.tooltip.items.foodSensitivities}}</li><li>{{decisionTree.reasonForVisit.general-digestive-problems-or-questions.tooltip.items.hemorrhoids}}</li><li>{{decisionTree.reasonForVisit.general-digestive-problems-or-questions.tooltip.items.hepatitis}}</li><li>{{decisionTree.reasonForVisit.general-digestive-problems-or-questions.tooltip.items.abnormalTestResults}}</li><li>{{decisionTree.reasonForVisit.general-digestive-problems-or-questions.tooltip.items.screeningForColonoscopy}}</li></ul></div>',
              tooltipAriaLabel:
                'decisionTree.reasonForVisit.general-digestive-problems-or-questions.tooltip.ariaLabel',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'inflammatory-bowel-disease',
              label:
                'decisionTree.reasonForVisit.inflammatory-bowel-disease.label',
              subCopy:
                'decisionTree.reasonForVisit.inflammatory-bowel-disease.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'serious-and-urgent-digestive-problems',
              label:
                'decisionTree.reasonForVisit.serious-and-urgent-digestive-problems.label',
              subCopy:
                'decisionTree.reasonForVisit.serious-and-urgent-digestive-problems.description',
              nextScene: 'gastroenterology-urgent-error',
              tooltip:
                '<div class="space-y-2"><div class="space-y-1"><p class="text-body-medium">{{decisionTree.reasonForVisit.serious-and-urgent-digestive-problems.tooltip.description}}</p></div><ul class="list-disc space-y-1 pl-5 text-body-medium"><li>{{decisionTree.reasonForVisit.serious-and-urgent-digestive-problems.tooltip.items.vomiting}}</li><li>{{decisionTree.reasonForVisit.serious-and-urgent-digestive-problems.tooltip.items.severePain}}</li><li>{{decisionTree.reasonForVisit.serious-and-urgent-digestive-problems.tooltip.items.painWithFever}}</li><li>{{decisionTree.reasonForVisit.serious-and-urgent-digestive-problems.tooltip.items.blackStools}}</li><li>{{decisionTree.reasonForVisit.serious-and-urgent-digestive-problems.tooltip.items.cancer}}</li><li>{{decisionTree.reasonForVisit.serious-and-urgent-digestive-problems.tooltip.items.lump}}</li><li>{{decisionTree.reasonForVisit.serious-and-urgent-digestive-problems.tooltip.items.blockage}}</li></ul></div>',
              tooltipAriaLabel:
                'decisionTree.reasonForVisit.serious-and-urgent-digestive-problems.tooltip.ariaLabel',
            },
          ],
        },
      },
      'gastroenterology-urgent-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'gastroenterology-urgent-error',
        props: {
          icon: 'error_fill',
          title: 'decisionTree.gastroenterologyUrgentError.title',
          body: 'decisionTree.gastroenterologyUrgentError.body',
          specialty: 'decisionTree.specialties.gastroenterology',
          ctaButtons: ['back-to-search'],
          progress: 100,
          name: 'gastroenterology-urgent-error',
          prevScene: 'gastroenterology-reason-for-visit',
        },
      },
      'reason-for-visit-provider-error': {
        type: 'decisionTreeError',
        component: modalComponents.RfvMismatchModal,
        sceneSlug: 'reason-for-visit-provider-error',
        props: {
          progress: 100,
          icon: 'error_fill',
          title: 'decisionTree.reasonForVisitProviderError.title',
          body: 'decisionTree.reasonForVisitProviderError.body',
          specialty: 'decisionTree.specialties.gastroenterology',
          ctaButtons: ['back-to-search-with-rfv'],
          name: 'reason-for-visit-provider-error',
          prevScene: 'gastroenterology-reason-for-visit',
        },
      },
      'select-time-modal': {
        type: 'decisionTreeForm',
        component: modalComponents.DecisionTreeForm,
        sceneSlug: 'select-time-modal',
        props: {
          progress: 85,
          name: 'select-time-modal',
          text: 'decisionTree.scheduleAppointmentWith',
          prevScene: 'gastroenterology-reason-for-visit',
          nextScene: 'patient-info',
        },
      },
    },
    orthopedics: {
      'is-adult': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'is-adult',
        props: {
          progress: 15,
          name: 'is-adult',
          text: 'decisionTree.isAdult.question',
          warning: 'decisionTree.isAdult.emergencySymptoms',
          prevScene: null,
          options: [
            {
              value: 'under18',
              label: 'decisionTree.isAdult.under18',
              nextScene: 'is-adult-ortho-error',
            },
            {
              value: 'between18and49',
              label: 'decisionTree.isAdult.between18and49',
              nextScene: 'seen-orthopedics-recently',
            },
            {
              value: 'over50',
              label: 'decisionTree.isAdult.over50',
              nextScene: 'seen-orthopedics-recently',
            },
          ],
        },
      },

      'is-adult-ortho-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'is-adult-ortho-error',
        props: {
          icon: 'error_fill',
          title: 'decisionTree.isAdultOrthopedicsError.title',
          body: 'decisionTree.isAdultOrthopedicsError.body',
          specialty: 'decisionTree.specialties.orthopedics',
          ctaButtons: ['back-to-search'],
          progress: 100,
          name: 'is-adult-ortho-error',
          prevScene: 'is-adult',
        },
      },

      'seen-orthopedics-recently': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'seen-orthopedics-recently',
        props: {
          progress: 30,
          name: 'seen-orthopedics-recently',
          text: 'decisionTree.seenProviderRecently.orthopedicsQuestion',
          prevScene: 'is-adult',
          specialty: 'decisionTree.specialties.orthopedics',
          options: [
            {
              value: 'yes',
              label: 'yes',
              nextScene: 'seen-ortho-recently-error',
            },
            { value: 'no', label: 'no', nextScene: 'ortho-workers-comp' },
          ],
        },
      },

      'seen-ortho-recently-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'seen-ortho-recently-error',
        props: {
          icon: 'error_fill',
          title: 'decisionTree.seenOrthopedicsRecentlyError.title',
          body: 'decisionTree.seenOrthopedicsRecentlyError.body',
          specialty: 'decisionTree.specialties.orthopedics',
          ctaButtons: ['login-to-mychart', 'back-to-search'],
          progress: 100,
          name: 'seen-ortho-recently-error',
          prevScene: 'seen-orthopedics-recently',
        },
      },

      'ortho-workers-comp': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'ortho-workers-comp',
        props: {
          progress: 45,
          name: 'ortho-workers-comp',
          text: 'decisionTree.workersComp.question',
          prevScene: 'seen-orthopedics-recently',
          options: [
            {
              value: 'yes',
              label: 'yes',
              nextScene: 'ortho-workers-comp-error',
            },
            { value: 'no', label: 'no', nextScene: 'ortho-reason-for-visit' },
          ],
        },
      },

      'ortho-workers-comp-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'ortho-workers-comp-error',
        props: {
          icon: 'error_fill',
          title: 'decisionTree.workersCompError.title',
          body: 'decisionTree.workersCompError.body',
          specialty: 'decisionTree.specialties.orthopedics',
          ctaButtons: ['back-to-search'],
          progress: 100,
          name: 'ortho-workers-comp-error',
          prevScene: 'ortho-workers-comp',
        },
      },

      'ortho-reason-for-visit': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'ortho-reason-for-visit',
        props: {
          progress: 60,
          name: 'ortho-reason-for-visit',
          text: 'decisionTree.reasonForVisit.orthopedics.part1Question',
          prevScene: 'ortho-workers-comp',
          options: [
            {
              value: 'neck-spine-or-back',
              label: 'decisionTree.reasonForVisit.neck-spine-or-back.label',
              subCopy:
                'decisionTree.reasonForVisit.neck-spine-or-back.description',
              nextScene: 'select-time-modal',
              action: 'setRfv',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'shoulder',
              label: 'decisionTree.reasonForVisit.orthopedics.areas.shoulder',
              nextScene: 'ortho-shoulder',
            },
            {
              value: 'arm-elbow-hand-wrist',
              label:
                'decisionTree.reasonForVisit.orthopedics.areas.arm-elbow-hand-wrist',
              nextScene: 'ortho-arm-elbow-hand-wrist',
            },
            {
              value: 'hip',
              label: 'decisionTree.reasonForVisit.orthopedics.areas.hip',
              nextScene: 'ortho-hip',
            },
            {
              value: 'knee',
              label: 'decisionTree.reasonForVisit.orthopedics.areas.knee',
              nextScene: 'ortho-knee',
            },
            {
              value: 'foot-ankle',
              label: 'decisionTree.reasonForVisit.orthopedics.areas.foot-ankle',
              nextScene: 'ortho-foot-ankle',
            },
            {
              value: 'sports-medicine-consultation',
              label:
                'decisionTree.reasonForVisit.sports-medicine-consultation.label',
              subCopy:
                'decisionTree.reasonForVisit.sports-medicine-consultation.description',
              nextScene: 'ortho-sports-medicine',
            },
            {
              value: 'oncology',
              label: 'decisionTree.reasonForVisit.oncology.label',
              subCopy: 'decisionTree.reasonForVisit.oncology.description',
              nextScene: 'select-time-modal',
              action: 'setRfv',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
          ],
        },
      },

      // SHOULDER
      'ortho-shoulder': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'ortho-shoulder',
        props: {
          progress: 75,
          name: 'ortho-shoulder',
          text: 'decisionTree.reasonForVisit.orthopedics.additionalRFVQuestion',
          prevScene: 'ortho-reason-for-visit',
          action: 'setRfv',
          options: [
            {
              value: 'shoulder-fracture',
              label: 'decisionTree.reasonForVisit.shoulder-fracture.label',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'shoulder-arthritis',
              label: 'decisionTree.reasonForVisit.shoulder-arthritis.label',
              nextScene: 'select-time-modal',
              action: 'rfvAddAgeGroup',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'shoulder-replacement',
              label: 'decisionTree.reasonForVisit.shoulder-replacement.label',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'something-else-shoulder',
              label:
                'decisionTree.reasonForVisit.something-else-shoulder.label',
              subCopy:
                'decisionTree.reasonForVisit.something-else-shoulder.description',
              nextScene: 'select-time-modal',
              action: 'rfvAddAgeGroup',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
          ],
        },
      },

      // ARM / ELBOW / HAND / WRIST
      'ortho-arm-elbow-hand-wrist': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'ortho-arm-elbow-hand-wrist',
        props: {
          progress: 75,
          name: 'ortho-arm-elbow-hand-wrist',
          text: 'decisionTree.reasonForVisit.orthopedics.additionalRFVQuestion',
          prevScene: 'ortho-reason-for-visit',
          action: 'setRfv',
          options: [
            {
              value: 'elbow-fracture',
              label: 'decisionTree.reasonForVisit.elbow-fracture.label',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'hand-or-finger-fracture',
              label:
                'decisionTree.reasonForVisit.hand-or-finger-fracture.label',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'wrist-fracture',
              label: 'decisionTree.reasonForVisit.wrist-fracture.label',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'elbow-pain-or-tennis-elbow',
              label:
                'decisionTree.reasonForVisit.elbow-pain-or-tennis-elbow.label',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'carpal-tunnel',
              label: 'decisionTree.reasonForVisit.carpal-tunnel.label',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'wrist-injury-or-pain',
              label: 'decisionTree.reasonForVisit.wrist-injury-or-pain.label',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'brachial-plexus-injury',
              label: 'decisionTree.reasonForVisit.brachial-plexus-injury.label',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'something-else-arm-elbow-hand-or-wrist',
              label:
                'decisionTree.reasonForVisit.something-else-arm-elbow-hand-or-wrist.label',
              subCopy:
                'decisionTree.reasonForVisit.something-else-arm-elbow-hand-or-wrist.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
          ],
        },
      },

      // HIP
      'ortho-hip': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'ortho-hip',
        props: {
          progress: 75,
          name: 'ortho-hip',
          text: 'decisionTree.reasonForVisit.orthopedics.additionalRFVQuestion',
          prevScene: 'ortho-reason-for-visit',
          action: 'setRfv',
          options: [
            {
              value: 'hip-arthritis',
              label: 'decisionTree.reasonForVisit.hip-arthritis.label',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'hip-replacement',
              label: 'decisionTree.reasonForVisit.hip-replacement.label',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'hip-issue',
              label: 'decisionTree.reasonForVisit.hip-issue.label',
              nextScene: 'select-time-modal',
              action: 'rfvAddAgeGroup',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
          ],
        },
      },

      // KNEE
      'ortho-knee': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'ortho-knee',
        props: {
          progress: 75,
          name: 'ortho-knee',
          text: 'decisionTree.reasonForVisit.orthopedics.additionalRFVQuestion',
          prevScene: 'ortho-reason-for-visit',
          action: 'setRfv',
          options: [
            {
              value: 'knee-pain',
              label: 'decisionTree.reasonForVisit.knee-pain.label',
              nextScene: 'select-time-modal',
              action: 'rfvAddAgeGroup',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'calf-pain',
              label: 'decisionTree.reasonForVisit.calf-pain.label',
              nextScene: 'select-time-modal',
              action: 'rfvAddAgeGroup',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'knee-replacement',
              label: 'decisionTree.reasonForVisit.knee-replacement.label',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'knee-fracture',
              label: 'decisionTree.reasonForVisit.knee-fracture.label',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'something-else-knee',
              label: 'decisionTree.reasonForVisit.something-else-knee.label',
              subCopy:
                'decisionTree.reasonForVisit.something-else-knee.description',
              nextScene: 'select-time-modal',
              action: 'rfvAddAgeGroup',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
          ],
        },
      },

      // FOOT / ANKLE
      'ortho-foot-ankle': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'ortho-foot-ankle',
        props: {
          progress: 75,
          name: 'ortho-foot-ankle',
          text: 'decisionTree.reasonForVisit.orthopedics.additionalRFVQuestion',
          prevScene: 'ortho-reason-for-visit',
          action: 'setRfv',
          options: [
            {
              value: 'ankle-injury',
              label: 'decisionTree.reasonForVisit.ankle-injury.label',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'ankle-pain',
              label: 'decisionTree.reasonForVisit.ankle-pain.label',
              nextScene: 'reason-for-visit-medicaid',
            },
            {
              value: 'bunion-or-heel-spurs',
              label: 'decisionTree.reasonForVisit.bunion-or-heel-spurs.label',
              nextScene: 'reason-for-visit-medicaid',
            },
            {
              value: 'foot-care',
              label: 'decisionTree.reasonForVisit.foot-care.label',
              subCopy: 'decisionTree.reasonForVisit.foot-care.description',
              nextScene: 'reason-for-visit-ortho-foot-care-medicaid',
            },
            {
              value: 'something-else-foot',
              label: 'decisionTree.reasonForVisit.something-else-foot.label',
              subCopy:
                'decisionTree.reasonForVisit.something-else-foot.description',
              nextScene: 'reason-for-visit-medicaid',
            },
          ],
        },
      },

      'reason-for-visit-medicaid': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'reason-for-visit-medicaid',
        props: {
          progress: 85,
          name: 'reason-for-visit-medicaid',
          text: 'decisionTree.reasonForVisit.orthopedics.medicaidQuestion.label',
          prevScene: 'ortho-foot-ankle',
          options: [
            {
              value: 'medicaid',
              label:
                'decisionTree.reasonForVisit.orthopedics.medicaidQuestion.options.yes',
              nextScene: 'select-time-modal',
              action: 'rfvAppendValue',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'no',
              label:
                'decisionTree.reasonForVisit.orthopedics.medicaidQuestion.options.no',
              nextScene: 'select-time-modal',
              action: 'rfvAppendValue',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
          ],
        },
      },

      'reason-for-visit-ortho-foot-care-medicaid': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'reason-for-visit-ortho-foot-care-medicaid',
        props: {
          progress: 85,
          name: 'reason-for-visit-ortho-foot-care-medicaid',
          text: 'decisionTree.reasonForVisit.orthopedics.medicaidQuestion.label',
          prevScene: 'ortho-foot-ankle',
          options: [
            {
              value: 'medicaid',
              label:
                'decisionTree.reasonForVisit.orthopedics.medicaidQuestion.options.yes',
              nextScene: 'foot-care-podiatry-error',
            },
            {
              value: 'no',
              label:
                'decisionTree.reasonForVisit.orthopedics.medicaidQuestion.options.no',
              nextScene: 'select-time-modal',
              action: 'rfvAppendValue',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
          ],
        },
      },

      'foot-care-podiatry-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'foot-care-podiatry-error',
        props: {
          icon: 'error_fill',
          title:
            'decisionTree.reasonForVisit.orthopedics.footCarePodiatry.title',
          body: 'decisionTree.reasonForVisit.orthopedics.footCarePodiatry.body',
          ctaButtons: ['back-to-search'],
          progress: 100,
          name: 'foot-care-podiatry-error',
          prevScene: 'reason-for-visit-ortho-foot-care-medicaid',
        },
      },

      // SPORTS MEDICINE
      'ortho-sports-medicine': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'ortho-sports-medicine',
        props: {
          progress: 75,
          name: 'ortho-sports-medicine',
          text: 'decisionTree.reasonForVisit.orthopedics.additionalRFVQuestion',
          prevScene: 'ortho-reason-for-visit',
          action: 'setRfv',
          options: [
            {
              value: 'sports-medicine-consultation',
              label:
                'decisionTree.reasonForVisit.sports-medicine-consultation.label',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'leg-fracture',
              label: 'decisionTree.reasonForVisit.leg-fracture.label',
              nextScene: 'select-time-modal',
              action: 'rfvAddAgeGroup',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'hamstring-or-quadriceps-injury',
              label:
                'decisionTree.reasonForVisit.hamstring-or-quadriceps-injury.label',
              nextScene: 'select-time-modal',
              action: 'rfvAddAgeGroup',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
          ],
        },
      },

      'reason-for-visit-provider-error': {
        type: 'decisionTreeError',
        component: modalComponents.RfvMismatchModal,
        sceneSlug: 'reason-for-visit-provider-error',
        props: {
          progress: 100,
          icon: 'error_fill',
          title: 'decisionTree.reasonForVisitProviderError.title',
          body: 'decisionTree.reasonForVisitProviderError.body',
          ctaButtons: ['back-to-search-with-rfv'],
          name: 'reason-for-visit-provider-error',
          prevScene: 'reason-for-visit',
        },
      },

      'select-time-modal': {
        type: 'decisionTreeForm',
        component: modalComponents.DecisionTreeForm,
        sceneSlug: 'select-time-modal',
        props: {
          progress: 90,
          name: 'select-time-modal',
          text: 'decisionTree.scheduleAppointmentWith',
          prevScene: 'reason-for-visit',
          nextScene: 'patient-info',
        },
      },
    },
    pediatrics: {
      'is-adult': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'is-adult',
        props: {
          progress: 25,
          name: 'is-adult',
          text: 'decisionTree.pcpPediatrics.patientAge.question',
          warning: 'decisionTree.isAdult.emergencySymptoms',
          prevScene: null,
          options: [
            {
              value: 'newborn',
              label: 'decisionTree.pcpPediatrics.patientAge.newborn',
              subCopy:
                'decisionTree.pcpPediatrics.patientAge.newbornDescription',
              nextScene: 'pediatrics-newborn-error',
            },
            {
              value: 'under18',
              label: 'decisionTree.pcpPediatrics.patientAge.under18',
              nextScene: 'pediatrics-seen-recently',
            },
            {
              value: 'over18',
              label: 'decisionTree.pcpPediatrics.patientAge.over18',
              nextScene: 'pediatrics-seen-recently',
            },
          ],
        },
      },
      'pediatrics-newborn-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'pediatrics-newborn-error',
        props: {
          progress: 25,
          icon: 'error_fill',
          name: 'pediatrics-newborn-error',
          title: 'decisionTree.pcpPediatrics.newbornError.title',
          body: 'decisionTree.pcpPediatrics.newbornError.body',
          specialty: 'decisionTree.specialties.primaryCare',
          ctaButtons: ['back-to-search'],
          prevScene: 'is-adult',
        },
      },
      'pediatrics-seen-recently': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'pediatrics-seen-recently',
        props: {
          progress: 50,
          name: 'pediatrics-seen-recently',
          text: 'decisionTree.seenProviderRecently.pediatricQuestion',
          specialty: 'decisionTree.specialties.pediatrics',
          prevScene: 'is-adult',
          options: [
            {
              value: 'yes',
              label: 'yes',
              nextScene: 'pediatrics-established-pediatric-error',
              validation: {
                type: 'pcpPediatricsEstablishedPatientRouting',
                failedValidationScene: 'pediatrics-established-adult-error',
              },
            },
            {
              value: 'no',
              label: 'no',
              // Age-based routing: under18 → pediatric RFV, over18 → adult RFV
              nextScene: 'pediatrics-reason-for-visit-pediatric',
              validation: {
                type: 'pcpPediatricsNewPatientRouting',
                failedValidationScene: 'pediatrics-reason-for-visit-adult',
              },
            },
          ],
        },
      },
      'pediatrics-established-pediatric-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'pediatrics-established-pediatric-error',
        props: {
          progress: 50,
          name: 'pediatrics-established-pediatric-error',
          icon: 'error_fill',
          title: 'decisionTree.pcpPediatrics.establishedPediatricError.title',
          body: 'decisionTree.pcpPediatrics.establishedPediatricError.body',
          specialty: 'decisionTree.specialties.pediatrics',
          ctaButtons: ['login-to-mychart', 'back-to-search'],
          prevScene: 'pediatrics-seen-recently',
        },
      },
      'pediatrics-established-adult-error': {
        type: 'decisionTreeError',
        component: modalComponents.DecisionTreeError,
        sceneSlug: 'pediatrics-established-adult-error',
        props: {
          progress: 50,
          name: 'pediatrics-established-adult-error',
          icon: 'error_fill',
          title: 'decisionTree.pcpPediatrics.establishedAdultError.title',
          body: 'decisionTree.pcpPediatrics.establishedAdultError.body',
          specialty: 'decisionTree.specialties.primaryCare',
          ctaButtons: ['login-to-mychart', 'back-to-search'],
          prevScene: 'pediatrics-seen-recently',
        },
      },
      'pediatrics-reason-for-visit-pediatric': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'pediatrics-reason-for-visit-pediatric',
        props: {
          progress: 75,
          name: 'pediatrics-reason-for-visit-pediatric',
          text: 'decisionTree.pcpPediatrics.reasonForVisit.question',
          prevScene: 'pediatrics-seen-recently',
          action: 'setRfv',
          options: [
            {
              value: 'sick-visit-under-18',
              label:
                'decisionTree.pcpPediatrics.reasonForVisit.sickVisit.label',
              subCopy:
                'decisionTree.pcpPediatrics.reasonForVisit.sickVisit.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'wellness-visit-under-18',
              label:
                'decisionTree.pcpPediatrics.reasonForVisit.wellnessVisit.label',
              subCopy:
                'decisionTree.pcpPediatrics.reasonForVisit.wellnessVisit.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
          ],
        },
      },
      'pediatrics-reason-for-visit-adult': {
        type: 'decisionTreeQuestion',
        component: modalComponents.DecisionTreeQuestion,
        sceneSlug: 'pediatrics-reason-for-visit-adult',
        props: {
          progress: 75,
          name: 'pediatrics-reason-for-visit-adult',
          text: 'decisionTree.reasonForVisit.question',
          prevScene: 'pediatrics-seen-recently',
          action: 'setRfv',
          options: [
            {
              value: 'office-visit-18-and-older',
              label:
                'decisionTree.pcpPediatrics.reasonForVisit.officeVisit.label',
              subCopy:
                'decisionTree.pcpPediatrics.reasonForVisit.officeVisit.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
            {
              value: 'annual-physical-18-and-older',
              label:
                'decisionTree.pcpPediatrics.reasonForVisit.annualPhysical.label',
              subCopy:
                'decisionTree.pcpPediatrics.reasonForVisit.annualPhysical.description',
              nextScene: 'select-time-modal',
              validation: {
                type: 'rfvSupportedByProvider',
                failedValidationScene: 'reason-for-visit-provider-error',
              },
            },
          ],
        },
      },
      'reason-for-visit-provider-error': {
        type: 'decisionTreeError',
        component: modalComponents.RfvMismatchModal,
        sceneSlug: 'reason-for-visit-provider-error',
        props: {
          progress: 100,
          icon: 'error_fill',
          title: 'decisionTree.reasonForVisitProviderError.title',
          body: 'decisionTree.reasonForVisitProviderError.body',
          specialty: 'decisionTree.specialties.primaryCare',
          ctaButtons: ['back-to-search-with-rfv'],
          name: 'reason-for-visit-provider-error',
          prevScene: 'pediatrics-reason-for-visit-pediatric',
        },
      },
      'select-time-modal': {
        type: 'decisionTreeForm',
        component: modalComponents.DecisionTreeForm,
        sceneSlug: 'select-time-modal',
        props: {
          progress: 100,
          name: 'select-time-modal',
          text: 'decisionTree.scheduleAppointmentWith',
          prevScene: null, // Can be reached from multiple scenes
          nextScene: 'patient-info',
        },
      },
    },
  };

  decisionTreeScenesCache[cacheKey] = scenes;

  return scenes;
};

export const appointmentRequestScenes: ScheduleScene[] = [
  {
    component: sceneComponents.SelectAppointmentSlot,
    sceneSlug: 'select-time',
    sceneName: 'schedule.selectTime',
    shouldShow: true,
    showProgressBar: true,
  },
  {
    component: sceneComponents.PatientInfo,
    sceneSlug: 'patient-info',
    sceneName: 'schedule.patientInfo.breadcrumb',
    shouldShow: true,
    showProgressBar: true,
  },
  {
    component: sceneComponents.Insurance,
    sceneSlug: 'insurance',
    sceneName: 'schedule.insurance',
    shouldShow: true,
    showProgressBar: true,
    onBreadcrumbClick: (context: ScheduleContext): void => {
      context.updateAppointment(EMPTY_INSURANCE_FIELDS);
    },
  },
  {
    component: sceneComponents.Review,
    sceneSlug: 'review',
    sceneName: 'schedule.review',
    shouldShow: true,
    showProgressBar: true,
  },
  {
    component: sceneComponents.Confirm,
    sceneSlug: 'confirm',
    sceneName: 'schedule.confirm',
    shouldShow: false,
    showProgressBar: false,
  },
];
export { SpecialtyBranch };
